import { tracker } from './SessionRecorder';

// On-screen annotation overlay for on-demand recording sessions. Strokes and
// text are drawn as SVG elements in the DOM, so rrweb captures every mutation
// and the annotations show up in the replay. The toolbar is only shown while a
// recording is active (driven by SessionRecorderButton) and only on the
// on-demand host.

const SVG_NS = 'http://www.w3.org/2000/svg';
const OVERLAY_ID = 'tracium-annot-overlay';
const TOOLBAR_ID = 'tracium-annot-toolbar';
const COLORS = ['#ef4444', '#f59e0b', '#10b981', '#3b82f6', '#111827', '#ffffff'];

type Tool = 'none' | 'pencil' | 'arrow' | 'text';

let overlay: SVGSVGElement | null = null;
let group: SVGGElement | null = null;
let toolbar: HTMLDivElement | null = null;
let toolButtons: { cursor: HTMLButtonElement; pencil: HTMLButtonElement; arrow: HTMLButtonElement; text: HTMLButtonElement } | null = null;

let tool: Tool = 'none';
let color: string = COLORS[0];
const history: SVGElement[] = [];

let drawing = false;
let activePath: SVGPathElement | null = null;
let arrowStart: { x: number; y: number } | null = null;
let textInput: HTMLInputElement | null = null;

// Build the path data for an arrow: a shaft plus two head segments at the tip,
// so the whole thing takes the current stroke colour (no per-colour markers).
const arrowPath = (x0: number, y0: number, x1: number, y1: number): string => {
	const angle = Math.atan2(y1 - y0, x1 - x0);
	const head = 14;
	const spread = Math.PI / 7;
	const hx1 = x1 - head * Math.cos(angle - spread);
	const hy1 = y1 - head * Math.sin(angle - spread);
	const hx2 = x1 - head * Math.cos(angle + spread);
	const hy2 = y1 - head * Math.sin(angle + spread);
	return `M ${x0} ${y0} L ${x1} ${y1} M ${x1} ${y1} L ${hx1} ${hy1} M ${x1} ${y1} L ${hx2} ${hy2}`;
};

const svg = <K extends keyof SVGElementTagNameMap>(name: K): SVGElementTagNameMap[K] =>
	document.createElementNS(SVG_NS, name);

const setOverlayInteractive = (on: boolean): void => {
	if (overlay) overlay.style.pointerEvents = on ? 'auto' : 'none';
};

const setTool = (next: Tool): void => {
	tool = next;
	setOverlayInteractive(tool !== 'none');
	if (toolButtons) {
		toolButtons.cursor.style.background = tool === 'none' ? '#2563eb' : '#374151';
		toolButtons.pencil.style.background = tool === 'pencil' ? '#2563eb' : '#374151';
		toolButtons.arrow.style.background = tool === 'arrow' ? '#2563eb' : '#374151';
		toolButtons.text.style.background = tool === 'text' ? '#2563eb' : '#374151';
	}
	if (overlay) overlay.style.cursor = tool === 'text' ? 'text' : tool === 'none' ? 'default' : 'crosshair';
};

const commitTextInput = (): void => {
	if (!textInput || !group) { textInput = null; return; }
	const value = textInput.value.trim();
	const x = Number(textInput.dataset.x);
	const y = Number(textInput.dataset.y);
	const stroke = textInput.dataset.color || color;
	textInput.remove();
	textInput = null;
	if (!value) return;

	const text = svg('text');
	text.setAttribute('x', String(x));
	text.setAttribute('y', String(y + 14));
	text.setAttribute('fill', stroke);
	text.setAttribute('font-size', '16');
	text.setAttribute('font-family', 'system-ui, sans-serif');
	text.setAttribute('font-weight', '600');
	text.textContent = value;
	group.appendChild(text);
	history.push(text);
};

const placeTextInput = (x: number, y: number): void => {
	commitTextInput();
	const input = document.createElement('input');
	input.type = 'text';
	input.dataset.x = String(x);
	input.dataset.y = String(y);
	input.dataset.color = color;
	Object.assign(input.style, {
		position: 'fixed', left: `${x}px`, top: `${y}px`, zIndex: '2147483002',
		background: 'transparent', color, border: `1px dashed ${color}`,
		font: '600 16px/1 system-ui, sans-serif', padding: '1px 3px', outline: 'none'
	} as CSSStyleDeclaration);
	input.addEventListener('keydown', e => {
		if (e.key === 'Enter') { commitTextInput(); }
		else if (e.key === 'Escape') { input.remove(); textInput = null; }
	});
	input.addEventListener('blur', () => commitTextInput());
	document.body.appendChild(input);
	textInput = input;
	setTimeout(() => input.focus(), 0);
};

const onPointerDown = (e: PointerEvent): void => {
	if (!group || !overlay) return;
	if (tool === 'pencil' || tool === 'arrow') {
		drawing = true;
		activePath = svg('path');
		activePath.setAttribute('fill', 'none');
		activePath.setAttribute('stroke', color);
		activePath.setAttribute('stroke-width', '3');
		activePath.setAttribute('stroke-linecap', 'round');
		activePath.setAttribute('stroke-linejoin', 'round');
		if (tool === 'arrow') {
			arrowStart = { x: e.clientX, y: e.clientY };
			activePath.setAttribute('d', arrowPath(e.clientX, e.clientY, e.clientX, e.clientY));
		} else {
			activePath.setAttribute('d', `M ${e.clientX} ${e.clientY}`);
		}
		group.appendChild(activePath);
		try { overlay.setPointerCapture(e.pointerId); } catch { /* ignore */ }
	} else if (tool === 'text') {
		placeTextInput(e.clientX, e.clientY);
	}
};

const onPointerMove = (e: PointerEvent): void => {
	if (!drawing || !activePath) return;
	if (tool === 'arrow' && arrowStart) {
		activePath.setAttribute('d', arrowPath(arrowStart.x, arrowStart.y, e.clientX, e.clientY));
	} else {
		activePath.setAttribute('d', `${activePath.getAttribute('d')} L ${e.clientX} ${e.clientY}`);
	}
};

const onPointerUp = (): void => {
	if (drawing && activePath) {
		history.push(activePath);
	}
	drawing = false;
	activePath = null;
	arrowStart = null;
};

const undo = (): void => {
	const last = history.pop();
	if (last) last.remove();
};

const clearAll = (): void => {
	while (history.length) history.pop()!.remove();
	if (textInput) { textInput.remove(); textInput = null; }
};

const iconButton = (label: string, title: string): HTMLButtonElement => {
	const b = document.createElement('button');
	b.type = 'button';
	b.textContent = label;
	b.title = title;
	Object.assign(b.style, {
		width: '30px', height: '30px', border: 'none', borderRadius: '6px',
		background: '#374151', color: '#fff', cursor: 'pointer',
		font: '600 13px/1 system-ui, sans-serif'
	} as CSSStyleDeclaration);
	return b;
};

const buildToolbar = (): void => {
	toolbar = document.createElement('div');
	toolbar.id = TOOLBAR_ID;
	Object.assign(toolbar.style, {
		position: 'fixed', bottom: '60px', right: '16px', zIndex: '2147483001',
		display: 'none', alignItems: 'center', gap: '6px',
		padding: '6px 8px', borderRadius: '10px', background: '#1f2937',
		boxShadow: '0 2px 12px rgba(0,0,0,.35)'
	} as CSSStyleDeclaration);

	// colour palette
	COLORS.forEach(c => {
		const sw = document.createElement('button');
		sw.type = 'button';
		sw.title = `Colour ${c}`;
		Object.assign(sw.style, {
			width: '20px', height: '20px', borderRadius: '50%', cursor: 'pointer',
			background: c, border: c === color ? '2px solid #fff' : '2px solid transparent'
		} as CSSStyleDeclaration);
		sw.addEventListener('click', () => {
			color = c;
			Array.from(toolbar!.querySelectorAll('button[data-swatch]')).forEach(el => {
				(el as HTMLElement).style.border = '2px solid transparent';
			});
			sw.style.border = '2px solid #fff';
		});
		sw.dataset.swatch = c;
		toolbar!.appendChild(sw);
	});

	const sep = document.createElement('span');
	Object.assign(sep.style, { width: '1px', height: '22px', background: '#4b5563', margin: '0 2px' } as CSSStyleDeclaration);
	toolbar.appendChild(sep);

	const cursor = iconButton('↖', 'Cursor — stop drawing, use the page (Esc)');
	const pencil = iconButton('✏', 'Pencil — draw freehand');
	const arrow = iconButton('↗', 'Arrow — drag from start to tip');
	const text = iconButton('T', 'Text — click to place a label');
	const undoBtn = iconButton('↶', 'Undo last annotation');
	const clearBtn = iconButton('✕', 'Clear all annotations');
	cursor.addEventListener('click', () => setTool('none'));
	pencil.addEventListener('click', () => setTool(tool === 'pencil' ? 'none' : 'pencil'));
	arrow.addEventListener('click', () => setTool(tool === 'arrow' ? 'none' : 'arrow'));
	text.addEventListener('click', () => setTool(tool === 'text' ? 'none' : 'text'));
	undoBtn.addEventListener('click', undo);
	clearBtn.addEventListener('click', clearAll);
	toolButtons = { cursor, pencil, arrow, text };
	[cursor, pencil, arrow, text, undoBtn, clearBtn].forEach(b => toolbar!.appendChild(b));

	document.body.appendChild(toolbar);
};

const buildOverlay = (): void => {
	overlay = svg('svg');
	overlay.id = OVERLAY_ID;
	Object.assign(overlay.style, {
		position: 'fixed', inset: '0', width: '100vw', height: '100vh',
		zIndex: '2147483000', pointerEvents: 'none'
	} as CSSStyleDeclaration);
	group = svg('g');
	overlay.appendChild(group);
	overlay.addEventListener('pointerdown', onPointerDown);
	overlay.addEventListener('pointermove', onPointerMove);
	overlay.addEventListener('pointerup', onPointerUp);
	document.body.appendChild(overlay);
};

// Esc exits any drawing tool back to cursor mode (unless a text label is being
// typed — there Esc cancels the label, handled on the input itself).
const onKeyDown = (e: KeyboardEvent): void => {
	if (e.key === 'Escape' && tool !== 'none' && !textInput) setTool('none');
};

const mount = (): void => {
	if (overlay && toolbar) return;
	if (typeof document === 'undefined' || !document.body) return;
	buildOverlay();
	buildToolbar();
	document.addEventListener('keydown', onKeyDown);
};

/** Show or hide the annotation toolbar/overlay. Hiding also resets the tool
 *  and clears the current annotations. Only active on the on-demand host. */
export const setAnnotatorVisible = (visible: boolean): void => {
	if (!tracker.isOnDemandHost()) return;
	mount();
	if (!toolbar || !overlay) return;
	if (visible) {
		toolbar.style.display = 'flex';
	} else {
		setTool('none');
		clearAll();
		toolbar.style.display = 'none';
	}
};
